import * as React from "react"

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { IconX } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

function Dialog<T = unknown>({
  ...props
}: DialogPrimitive.Root.Props<T> & { onClose?: () => void }) {
  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      {...props}
      onOpenChange={(open, e) => {
        props.onOpenChange?.(open, e)
        if (props.onClose !== undefined && !open) {
          props.onClose()
        }
      }}
    />
  )
}

Dialog.createHandle = DialogPrimitive.createHandle

function DialogTrigger<T = unknown>({ ...props }: DialogPrimitive.Trigger.Props<T>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      forceRender
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[closed]:animate-out data-[closed]:animation-duration-[200ms] data-[closed]:fade-out-0 data-[open]:animate-in data-[open]:fade-in-0",
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "fixed top-[50%] left-[50%] z-50 flex max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 rounded-lg border bg-white p-6 shadow-lg duration-200 data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="rounded-xs absolute top-4 right-4 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[open]:bg-accent data-[open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <IconX />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function DialogSimple<T = unknown>({
  title,
  description,
  triggerRender,
  actions,
  children,
  handle,
  onClose,
  onOpenChange,
  onOpenChangeComplete,
  open,
  className,
  disablePointerDismissal,
}: {
  title: string
  description?: string
  triggerRender?: DialogPrimitive.Trigger.Props["render"]
  actions?: React.ReactNode
  children?: DialogPrimitive.Root.Props<T>["children"]
  handle?: DialogPrimitive.Root.Props<T>["handle"]
  onClose?: () => void
  onOpenChange?: DialogPrimitive.Root.Props<T>["onOpenChange"]
  onOpenChangeComplete?: DialogPrimitive.Root.Props<T>["onOpenChangeComplete"]
  open?: boolean
  className?: string
  disablePointerDismissal?: DialogPrimitive.Root.Props<T>["disablePointerDismissal"]
}) {
  return (
    <Dialog
      open={open}
      handle={handle}
      onClose={onClose}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      disablePointerDismissal={disablePointerDismissal}
    >
      {typeof children == "function" ? (
        ({ payload }) => (
          <>
            {triggerRender && <DialogTrigger render={triggerRender} />}
            <DialogContent className={className}>
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
              </DialogHeader>
              {children({ payload })}
              <DialogFooter>{actions}</DialogFooter>
            </DialogContent>
          </>
        )
      ) : (
        <>
          {triggerRender && <DialogTrigger render={triggerRender} />}
          <DialogContent className={className}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
            {children}
            {actions && <DialogFooter>{actions}</DialogFooter>}
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DialogSimple,
}
